import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Settings, Users, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const { token } = useSelector((state) => state.auth);
  const [settings, setSettings] = useState({});
  const [guestStats, setGuestStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'https://api-invoice.ainixit.com/api';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/settings`, config),
        axios.get(`${API_URL}/admin/settings/guest-stats`, config),
      ]);

      setSettings(settingsRes.data.settings);
      setGuestStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = async (key, value) => {
    setIsSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/settings/${key}`,
        { value },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSettings(prev => ({
        ...prev,
        [key]: { ...prev[key], value }
      }));

      toast.success('Setting updated successfully');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Admin Settings</h1>
        <p className="text-gray-600 mt-1">Manage system settings and guest limits</p>
      </div>

      {/* Guest Statistics */}
      {guestStats && (
        <div className="card">
          <div className="flex items-center mb-6">
            <BarChart3 className="w-6 h-6 text-primary-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Guest Usage Statistics</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{guestStats.stats.total_guest_users}</p>
              <p className="text-sm text-gray-600 mt-1">Total Guest Users</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{guestStats.stats.active_guest_users}</p>
              <p className="text-sm text-gray-600 mt-1">Active This Month</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{guestStats.stats.total_guest_invoices}</p>
              <p className="text-sm text-gray-600 mt-1">Total Guest Invoices</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-900">{guestStats.stats.guest_invoices_this_month}</p>
              <p className="text-sm text-gray-600 mt-1">This Month</p>
            </div>
          </div>

          {/* Top IPs */}
          {guestStats.top_ips && guestStats.top_ips.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top IP Addresses by Usage</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">IP Address</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Invoices Created</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guestStats.top_ips.map((ip, index) => (
                      <tr key={index} className="border-b last:border-0">
                        <td className="py-3 px-4 font-mono text-sm">{ip._id}</td>
                        <td className="py-3 px-4">{ip.invoices_created}</td>
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(ip.last_activity).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings */}
      <div className="card">
        <div className="flex items-center mb-6">
          <Settings className="w-6 h-6 text-primary-600 mr-2" />
          <h2 className="text-xl font-bold text-gray-900">System Settings</h2>
        </div>

        <div className="space-y-6">
          {/* Guest Monthly Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Guest Monthly Limit
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Number of invoices a guest user can create per month from the same IP address
            </p>
            <div className="flex items-center space-x-4">
              <input
                type="number"
                min="0"
                max="10"
                className="input w-32"
                value={settings.guest_monthly_limit?.value || 1}
                onChange={(e) => handleSettingChange('guest_monthly_limit', parseInt(e.target.value) || 0)}
                disabled={isSaving}
              />
              <span className="text-sm text-gray-600">invoices per month</span>
            </div>
          </div>

          {/* Watermark Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Watermark Text
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Text to display as watermark on PDF invoices for free plan users
            </p>
            <input
              type="text"
              className="input max-w-md"
              value={settings.watermark_text?.value || 'SAMPLE'}
              onChange={(e) => handleSettingChange('watermark_text', e.target.value)}
              disabled={isSaving}
              placeholder="SAMPLE"
            />
          </div>

          {/* Site Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Name
            </label>
            <p className="text-sm text-gray-600 mb-3">
              The name displayed on the landing page and throughout the application
            </p>
            <input
              type="text"
              className="input max-w-md"
              value={settings.site_name?.value || 'Ainix Invoice'}
              onChange={(e) => handleSettingChange('site_name', e.target.value)}
              disabled={isSaving}
            />
          </div>

          {/* Site Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Description
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Brief description shown on the landing page
            </p>
            <textarea
              className="input max-w-md"
              rows="3"
              value={settings.site_description?.value || 'Professional invoice generation made simple'}
              onChange={(e) => handleSettingChange('site_description', e.target.value)}
              disabled={isSaving}
            />
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Email address for customer support and inquiries
            </p>
            <input
              type="email"
              className="input max-w-md"
              value={settings.contact_email?.value || 'support@invoicesaas.com'}
              onChange={(e) => handleSettingChange('contact_email', e.target.value)}
              disabled={isSaving}
            />
          </div>

          {/* Maintenance Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maintenance Mode
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Enable maintenance mode to temporarily disable guest access
            </p>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={settings.maintenance_mode?.value || false}
                  onChange={(e) => handleSettingChange('maintenance_mode', e.target.checked)}
                  disabled={isSaving}
                />
                <span className="ml-2 text-sm text-gray-700">Enable maintenance mode</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Settings */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Additional Settings</h2>
        
        <div className="space-y-6">
          {/* Default Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Currency
            </label>
            <select
              className="input max-w-xs"
              value={settings.default_currency?.value || 'USD'}
              onChange={(e) => handleSettingChange('default_currency', e.target.value)}
              disabled={isSaving}
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
            </select>
          </div>

          {/* Default Tax Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Tax Rate (%)
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className="input w-32"
                value={settings.default_tax_rate?.value || 0}
                onChange={(e) => handleSettingChange('default_tax_rate', parseFloat(e.target.value) || 0)}
                disabled={isSaving}
              />
              <span className="text-sm text-gray-600">%</span>
            </div>
          </div>

          {/* Invoice Number Prefix */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invoice Number Prefix
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Prefix for generated invoice numbers (e.g., "INV", "BILL")
            </p>
            <input
              type="text"
              className="input max-w-xs"
              value={settings.invoice_prefix?.value || 'INV'}
              onChange={(e) => handleSettingChange('invoice_prefix', e.target.value)}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;

